import { supabase } from './supabase';

export interface SpeechAnalysisRequest {
  user_id: string;
  audio_file_path: string;
  original_text: string;
  language?: 'en' | 'vi';
}

export interface SpeechAnalysisResponse {
  success: boolean;
  data: {
    audio_file_path: string;
    transcription: string;
    overall_score: number;
    feedback_en: string;
    feedback_vi: string;
    suggestions_en: string;
    suggestions_vi: string;
    phoneme_scores: Array<{
      phoneme: string;
      score: number;
      feedback_en: string;
      feedback_vi: string;
    }>;
    language: string;
  };
  error?: string;
}

export interface SpeechAnalysisUploadRequest {
  audio: File;
  user_id: string;
  original_text: string;
  language?: 'en' | 'vi';
}

export class SpeechAnalysisService {
  private static readonly API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080';

  /**
   * Analyze speech using the backend API
   */
  static async analyzeSpeech(request: SpeechAnalysisRequest): Promise<SpeechAnalysisResponse> {
    try {
      const response = await fetch(`${this.API_BASE_URL}/api/speech/analyze`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${await this.getAuthToken()}`,
        },
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Speech analysis failed:', error);
      throw error;
    }
  }

  /**
   * Analyze speech with file upload
   */
  static async analyzeSpeechUpload(request: SpeechAnalysisUploadRequest): Promise<SpeechAnalysisResponse> {
    try {
      const formData = new FormData();
      formData.append('audio', request.audio);
      formData.append('user_id', request.user_id);
      formData.append('original_text', request.original_text);
      if (request.language) {
        formData.append('language', request.language);
      }

      const response = await fetch(`${this.API_BASE_URL}/api/speech/analyze/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${await this.getAuthToken()}`,
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Speech analysis upload failed:', error);
      throw error;
    }
  }

  /**
   * Test the speech analysis endpoint (public endpoint)
   */
  static async testSpeechAnalysis(): Promise<any> {
    try {
      const response = await fetch(`${this.API_BASE_URL}/api/test/speech-analysis`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Speech analysis test failed:', error);
      throw error;
    }
  }

  /**
   * Get backend status
   */
  static async getBackendStatus(): Promise<any> {
    try {
      const response = await fetch(`${this.API_BASE_URL}/api/test/status`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Backend status check failed:', error);
      throw error;
    }
  }

  /**
   * Convert audio blob to base64 data URL
   */
  static blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        if (typeof reader.result === 'string') {
          resolve(reader.result);
        } else {
          reject(new Error('Failed to convert blob to base64'));
        }
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  /**
   * Get authentication token from Supabase
   */
  private static async getAuthToken(): Promise<string> {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) {
      throw new Error('No authentication token available');
    }
    return session.access_token;
  }
}
